import {
	type Control,
	Controller,
	type FieldPath,
	type FieldValues,
} from "react-hook-form";
import type { FieldErrorProps } from "web_myplann/Forms/Hooks/useFieldError";
import {
	MyPlannInput,
	type MyPlannInputProps,
} from "web_myplann/Shared/Components/MyPlannInput";
import { NumberMyPlannInput } from "web_myplann/Shared/Components/NumberMyPlannInput";

type Props<FormType extends FieldValues, TName> = Omit<
	MyPlannInputProps,
	"type"
> &
	FieldErrorProps & {
		control: Control<FormType, object>;
		name: TName;
		onChange?: (
			event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		) => void;
		useNameForId?: boolean;
		autocomplete?: string;
		type?: MyPlannInputProps["type"] | "float" | "integer";
		decimalPlaces?: number;
	};

export const MyPlannFormInput = <
	FormType extends FieldValues = FieldValues,
	TName extends FieldPath<FormType> = FieldPath<FormType>,
>({
	control,
	name,
	useNameForId,
	autocomplete,
	type,
	decimalPlaces,
	...rest
}: Props<FormType, TName>) => {
	const isInteger = type === "integer";
	const isNumeric = type === "float" || isInteger;

	return (
		<Controller
			control={control}
			name={name}
			render={({
				field: { onChange, value, onBlur, ref },
				fieldState: { error },
			}) => {
				if (isNumeric) {
					return (
						<NumberMyPlannInput
							{...rest}
							mode={isInteger ? "integer" : "decimal"}
							decimalPlaces={decimalPlaces}
							value={value}
							onChange={onChange}
							onBlur={onBlur}
							inputRef={ref}
							error={!!error?.message}
							helperText={error?.message}
							autocomplete={autocomplete ?? "off"}
							id={useNameForId ? name : rest.id}
						/>
					);
				}

				return (
					<MyPlannInput
						value={value}
						multiline={rest.multiline ?? type !== "password"}
						error={!!error?.message}
						helperText={error?.message}
						onChange={(e) => {
							if (type === "number") {
								const numValue = Number(e.target.value);
								onChange(Number.isNaN(numValue) ? e.target.value : numValue);
							} else {
								onChange(e);
							}
						}}
						onBlur={onBlur}
						inputRef={ref}
						autocomplete={autocomplete ?? "off"}
						{...rest}
						type={type}
						id={useNameForId ? name : rest.id}
					/>
				);
			}}
		/>
	);
};
